import React, { useRef, useEffect } from 'react';
import { gsap } from '../../lib/gsapSetup';
import { useTranslation } from '../../i18n/LanguageContext';
import communaute from '../../assets/images/Untitled-1.webp';
import VERIFIEDDRIVER from '../../assets/images/VERIFIED_DRIVER.webp';
import technologie from '../../assets/images/Technologie_intelligente.webp';
import visibilityImage from '../../assets/images/COUVERTURE_nationale.webp';
/**
 * ECOSYSTEM — Cinematic image mosaic with parallax + clip reveals.
 *
 * Magazine-style layout: large hero image on left, stacked pair on right,
 * full-width banner at bottom. Each block has overlay text that reveals
 * on scroll. Heavy GSAP usage: clip-path reveals, parallax, stagger.
 */

const BLOCKS = [
  {
    key: 'coverage',
    image: visibilityImage,
    alt: 'Flotte de camions couvrant la Tunisie',
    layout: 'hero', // tall left column
  },
  {
    key: 'drivers',
    image: VERIFIEDDRIVER,
    alt: 'Transporteur professionnel vérifié',
    layout: 'top-right',
  },
  {
    key: 'tech',
    image: technologie,
    alt: 'Application GPS et suivi en temps réel',
    layout: 'bottom-right',
  },
  {
    key: 'community',
    image: communaute,
    alt: 'Communauté de livreurs LinxExpress',
    layout: 'banner', // full-width bottom
  },
];

export default function BentoGallery() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subRef = useRef(null);
  const { t, lang } = useTranslation();
  const textShadow = {
    textShadow: '0 2px 12px rgba(0, 0, 0, 0.7), 0 1px 4px rgba(0, 0, 0, 0.5)',
  };

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      /* ── heading reveal ── */
      if (headingRef.current) {
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: 'power3.out',
            scrollTrigger: { trigger: headingRef.current, start: 'top 85%' },
          },
        );
      }

      /* subtitle fade in */
      if (subRef.current) {
        gsap.fromTo(
          subRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: { trigger: subRef.current, start: 'top 88%' },
          },
        );
      }

      /* ── image blocks: clip-path reveal + parallax ── */
      const cards = sectionRef.current.querySelectorAll('.eco-card');
      cards.forEach((card, i) => {
        const img = card.querySelector('.eco-img');
        const overlay = card.querySelector('.eco-overlay');
        const text = card.querySelector('.eco-text');

        // Clip-path reveal — each card wipes in from a different direction
        const clips = [
          ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'], // left to right
          ['inset(100% 0 0 0)', 'inset(0% 0 0 0)'], // bottom to top
          ['inset(0 0 100% 0)', 'inset(0 0 0% 0)'], // top to bottom
          ['inset(0 0 0 100%)', 'inset(0 0 0 0%)'], // right to left
        ];
        const [clipFrom, clipTo] = clips[i % clips.length];

        gsap.fromTo(
          card,
          { clipPath: clipFrom, opacity: 0 },
          {
            clipPath: clipTo,
            opacity: 1,
            duration: 1.4,
            ease: 'power4.inOut',
            scrollTrigger: {
              trigger: card,
              start: 'top 88%',
            },
          },
        );

        // Image parallax inside each card
        if (img) {
          gsap.fromTo(
            img,
            { scale: 1.25, y: 40 },
            {
              scale: 1,
              y: -40,
              ease: 'none',
              scrollTrigger: {
                trigger: card,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
              },
            },
          );
        }

        // Overlay text slides up
        if (text) {
          gsap.fromTo(
            text,
            { opacity: 0, y: 40 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              delay: 0.3,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: card,
                start: 'top 75%',
              },
            },
          );
        }
      });

      /* ── floating stat badges ── */
      const badges = sectionRef.current.querySelectorAll('.eco-badge');
      badges.forEach((badge, i) => {
        gsap.fromTo(
          badge,
          { opacity: 0, scale: 0.7, y: 20 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.6,
            delay: 0.5 + i * 0.15,
            ease: 'back.out(2)',
            scrollTrigger: {
              trigger: badge.closest('.eco-card'),
              start: 'top 70%',
            },
          },
        );

        // Gentle float
        gsap.to(badge, {
          y: i % 2 === 0 ? -8 : 8,
          duration: 2.5 + i * 0.3,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className='relative py-16 sm:py-28 lg:py-40 bg-primary overflow-hidden'
    >
      {/* ── Background ── */}
      <div className='absolute inset-0 pointer-events-none'>
        <div
          className='absolute inset-0 opacity-[0.025]'
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,.4) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className='section-container relative z-10'>
        {/* ── Header ── */}
        <div className='text-center mb-10 sm:mb-16 lg:mb-24'>
          <span className='inline-block font-mono text-xs text-secondary tracking-[0.25em] uppercase mb-5'>
            {t('bento.label')}
          </span>
          <h2
            ref={headingRef}
            className='font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.05]'
            aria-label={t('bento.heading')}
          >
            {t('bento.heading')}
          </h2>
          <p
            ref={subRef}
            className='mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed'
            style={textShadow}
          >
            {t('bento.subtitle')}
          </p>
        </div>

        {/* ── Image mosaic ── */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5'>
          {/* LEFT — tall hero image */}
          <div className='eco-card relative rounded-2xl sm:rounded-3xl overflow-hidden h-[320px] sm:h-[420px] md:h-[500px] lg:h-[640px] group'>
            <img
              src={BLOCKS[0].image}
              alt={BLOCKS[0].alt}
              className='eco-img absolute inset-0 w-full h-[130%] object-cover will-change-transform'
              loading='lazy'
            />
            <div className='eco-overlay absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent' />

            {/* Floating badge */}
            <div className='eco-badge absolute top-4 left-4 sm:top-6 sm:left-6 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 flex items-center gap-2'>
              <span className='w-2 h-2 rounded-full bg-green-400 animate-pulse' />
              <span className='text-primary text-xs font-semibold tracking-wide'>
                {t('bento.badge.live')}
              </span>
            </div>

            <div className='eco-text absolute bottom-0 left-0 right-0 p-5 sm:p-8 lg:p-10'>
              <span className='font-display text-5xl sm:text-6xl lg:text-8xl font-bold text-white/10 absolute -top-8 sm:-top-12 left-5 sm:left-8 select-none pointer-events-none'>
                24
              </span>
              <h3
                className='font-display text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1.5 sm:mb-2'
                style={textShadow}
              >
                {t('bento.coverage.title')}
              </h3>
              <p
                className='text-xs sm:text-sm md:text-base text-white max-w-sm leading-relaxed'
                style={textShadow}
              >
                {t('bento.coverage.desc')}
              </p>
            </div>
          </div>

          {/* RIGHT — stacked pair */}
          <div className='flex flex-col gap-4 lg:gap-5'>
            {/* Top right */}
            <div className='eco-card relative rounded-2xl sm:rounded-3xl overflow-hidden h-[200px] sm:h-[240px] lg:h-[310px] group'>
              <img
                src={BLOCKS[1].image}
                alt={BLOCKS[1].alt}
                className='eco-img absolute inset-0 w-full h-[140%] object-cover will-change-transform'
                loading='lazy'
              />
              <div className='eco-overlay absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/40 to-transparent' />

              {/* Badge */}
              <div className='eco-badge absolute top-5 right-5 px-3 py-1.5 rounded-full bg-secondary/90 text-primary text-xs font-bold'>
                {t('bento.badge.verified')}
              </div>

              <div className='eco-text absolute bottom-0 left-0 right-0 p-4 sm:p-7 lg:p-8'>
                <h3
                  className='font-display text-lg sm:text-xl md:text-2xl font-bold text-white mb-1'
                  style={textShadow}
                >
                  {t('bento.drivers.title')}
                </h3>
                <p
                  className='text-xs sm:text-sm text-white max-w-xs leading-relaxed'
                  style={textShadow}
                >
                  {t('bento.drivers.desc')}
                </p>
              </div>
            </div>

            {/* Bottom right */}
            <div className='eco-card relative rounded-2xl sm:rounded-3xl overflow-hidden h-[200px] sm:h-[240px] lg:h-[310px] group'>
              <img
                src={BLOCKS[2].image}
                alt={BLOCKS[2].alt}
                className='eco-img absolute inset-0 w-full h-[140%] object-cover will-change-transform'
                loading='lazy'
              />
              <div className='eco-overlay absolute inset-0 bg-gradient-to-l from-primary/85 via-primary/40 to-transparent' />

              {/* Badge */}
              <div className='eco-badge absolute top-5 left-5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white text-xs font-semibold'>
                GPS · Chat · Appels
              </div>

              <div className='eco-text absolute bottom-0 left-0 right-0 p-4 sm:p-7 lg:p-8'>
                <h3
                  className='font-display text-lg sm:text-xl md:text-2xl font-bold text-white mb-1'
                  style={textShadow}
                >
                  {t('bento.tech.title')}
                </h3>
                <p
                  className='text-xs sm:text-sm text-white max-w-xs leading-relaxed'
                  style={textShadow}
                >
                  {t('bento.tech.desc')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Full-width banner ── */}
        <div className='eco-card relative rounded-2xl sm:rounded-3xl overflow-hidden h-[280px] sm:h-[300px] lg:h-[360px] mt-4 lg:mt-5 group'>
          <img
            src={BLOCKS[3].image}
            alt={BLOCKS[3].alt}
            className='eco-img absolute inset-0 w-full h-[140%] object-cover will-change-transform'
            loading='lazy'
          />
          <div className='eco-overlay absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/50 to-primary/20' />

          <div
            className='eco-text absolute inset-0 flex items-center p-5 sm:p-10 lg:p-16'
            dir={lang === 'ar' ? 'ltr' : undefined}
          >
            <div className={`max-w-lg ${lang === 'ar' ? 'text-left' : ''}`}>
              <h3
                className='font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3'
                style={textShadow}
              >
                {t('bento.community.title')}
              </h3>
              <p
                className='text-xs sm:text-sm md:text-base text-white leading-relaxed mb-4 sm:mb-6'
                style={textShadow}
              >
                {t('bento.community.desc')}
              </p>

              {/* Inline stats */}
              <div className='flex flex-wrap items-center gap-4 sm:gap-6 lg:gap-10'>
                <div>
                  <span className='font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary'>
                    24
                  </span>
                  <span
                    className='block text-[10px] sm:text-xs text-white/60 mt-0.5'
                    style={textShadow}
                  >
                    {t('bento.stat.coverage')}
                  </span>
                </div>
                <div className='w-[1px] h-10 bg-white/10' />
                <div>
                  <span className='font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary'>
                    24/7
                  </span>
                  <span
                    className='block text-[10px] sm:text-xs text-white/60 mt-0.5'
                    style={textShadow}
                  >
                    {t('bento.stat.speed')}
                  </span>
                </div>
                <div className='w-[1px] h-10 bg-white/10' />
                <div>
                  <span className='font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary'>
                    100%
                  </span>
                  <span
                    className='block text-[10px] sm:text-xs text-white/60 mt-0.5'
                    style={textShadow}
                  >
                    {t('bento.stat.trust')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
